import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { logError } from 'services/logging/logging';
import { useSubmitTask } from 'services/repositories/tasks/tasks';
import { addNotification, NotificationType } from 'stores/notifications/notifications';

type UseTaskSubmissionArgs = {
  relationshipId: string;
  taskId: string;
  onSuccess: () => void;
};

export const useTaskSubmission = ({ relationshipId, taskId, onSuccess }: UseTaskSubmissionArgs) => {
  const { t } = useTranslation();
  const { mutateAsync, isLoading: isSubmitting } = useSubmitTask();
  const queryClient = useQueryClient();

  const submitTask = useCallback(
    async (canonicalData: Record<string, unknown>) => {
      try {
        await mutateAsync({
          relationshipId,
          taskId,
          payload: { data: canonicalData },
        });
        await queryClient.invalidateQueries(['/relationships/:relationshipId/tasks']);
        onSuccess();
      } catch (error) {
        logError(error);
        addNotification(t('common:error.generic'), NotificationType.error);
      }
    },
    [mutateAsync, relationshipId, taskId, queryClient, onSuccess, t],
  );

  return { submitTask, isSubmitting };
};
